import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import api, { type EntitlementSummary } from '../services/api';
import { getDeviceTimezone } from '../utils/time';
import i18n, { normalizeLang } from '../i18n';
import { normalizeInviteToken } from '../utils/invite';

export interface User {
  id: string;
  telegram_id: string;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  role: 'owner' | 'admin' | 'tester' | 'user' | 'owner_school' | 'teacher';
  school_id?: string;
  timezone?: string;
  currency?: string;
  language_code?: string;
  language_manual?: boolean;
  needs_onboarding?: boolean;
  is_active: boolean;
  // Системный владелец SaaS (по telegram_id из OWNER_TELEGRAM_ID на бэкенде).
  // Панель владельца видна ТОЛЬКО ему — проверяется по id, а не по роли.
  is_system_owner?: boolean;
}

// ─── Единая система семантических прав доступа ────────────────────────────
// Этап 7: роль 'teacher' — урезанный user. Урезаем:
//   ученики (создание/редактирование/архивация),
//   курсы (создание/редактирование + цены), группы (создание/состав/расписание),
//   платежи (добавление/редактирование).
// teacher ОСТАЁТСЯ: просмотр всего, ведение занятий (посещаемость, ДЗ, статусы,
//   создание/отмена/перенос занятий) и достижения.
//
// Действие                     Teacher  User  Admin  Owner
// Просмотр учеников              ✅     ✅     ✅     ✅
// Создание/редактир. ученика     ❌     ✅     ✅     ✅
// Архивация ученика              ❌     ✅     ✅     ✅
// Курсы: создание/редактир.+цены ❌     ✅     ✅     ✅
// Группы: создание/состав        ❌     ✅     ✅     ✅
// Платежи: добавление/редакт.    ❌     ✅     ✅     ✅
// Ведение занятия (посещ., ДЗ)   ✅     ✅     ✅     ✅
// Создание/отмена занятий        ✅     ✅     ✅     ✅
// Достижения (создание)          ✅     ✅     ✅     ✅
// Управление пользователями      ❌     ❌     ✅     ✅
// Панель Owner                   ❌     ❌     ❌     ✅

export interface Permissions {
  canViewStudents: boolean;
  canViewStudentCard: boolean;
  canEditStudents: boolean;
  canArchiveStudents: boolean;
  canDeleteStudents: boolean;
  canAddPayments: boolean;
  canViewPayments: boolean;
  canManageUsers: boolean;
  canOpenOwnerPanel: boolean;
  canManageCourses: boolean;
  canManageGroups: boolean;
  canManageLessons: boolean;
  canManageAchievements: boolean;
  // ЭТАП 8: команда школы. Владелец школы и админ школы ведут команду
  // (участники, приглашение учителей, назначение уроков); учитель в команде
  // видит состав команды ТОЛЬКО для чтения; владельческие действия (смены
  // ролей, удаление, админ-приглашение, пояс школы) — только у владельца.
  canViewSchoolTeam: boolean;
  canManageSchoolTeam: boolean;
  canManageSchoolRoles: boolean;
}

function computePermissions(user?: User | null): Permissions {
  const role = user?.role;
  const isOwner = role === 'owner';
  const isAdmin = role === 'admin' || isOwner;
  const isOwnerSchool = role === 'owner_school';
  // Полный преподавательский доступ: user / owner_school / admin / owner
  const isFullTeacher = role === 'user' || isOwnerSchool || isAdmin;
  // Урезанная роль 'teacher' (этап 7) — ведёт занятия, но не редактирует картотеку
  const isRestrictedTeacher = role === 'teacher';
  // Ведение занятий доступно всем преподавательским ролям
  const canRunLessons = isFullTeacher || isRestrictedTeacher;

  return {
    canViewStudents: true,                    // Все аутентифицированные
    canViewStudentCard: true,                 // Все аутентифицированные
    canEditStudents: isFullTeacher,           // создание/редактирование учеников
    canArchiveStudents: isFullTeacher,        // архивация/восстановление
    canDeleteStudents: isOwner,               // Только Owner
    canAddPayments: isFullTeacher,            // добавление платежей
    canViewPayments: true,                    // Все аутентифицированные
    canManageUsers: isAdmin,                  // Admin+
    // Панель владельца видна ТОЛЬКО системному владельцу по telegram_id
    // (is_system_owner приходит с бэкенда) — роль 'owner' сама по себе
    // панель НЕ открывает.
    canOpenOwnerPanel: user?.is_system_owner === true,
    canManageCourses: isFullTeacher,          // создание/редактирование курсов + цены
    canManageGroups: isFullTeacher,           // создание/состав/расписание групп
    canManageLessons: canRunLessons,          // занятия: посещаемость, ДЗ, статусы
    canManageAchievements: canRunLessons,     // достижения
    canManageSchoolTeam: isOwnerSchool || isAdmin,   // команда школы
    canManageSchoolRoles: isOwnerSchool || isOwner,  // роли/удаление участников
    // Карточка «Команда» видна и учителю в команде (только просмотр): ему
    // нужно видеть состав команды. Без школы карточки нет ни у кого —
    // панель читает /api/school, а школы у такого пользователя просто нет.
    canViewSchoolTeam: !!user?.school_id
      && (isOwnerSchool || isAdmin || isRestrictedTeacher),
  };
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: () => Promise<void>;
  logout: () => void;
  completeOnboarding: (choice: 'solo' | 'team', schoolName?: string) => Promise<void>;
  upgradeToSchoolOwner: (schoolName: string) => Promise<void>;
  setUserTimezone: (timezone: string) => Promise<void>;
  setUserCurrency: (currency: string) => Promise<void>;
  setUserLanguage: (language: string) => Promise<void>;
  setSchoolTimezone: (timezone: string) => Promise<void>;
  inviteToken: string | null;
  joinSchool: (token: string) => Promise<void>;
  dismissInvite: () => void;
  isAdmin: boolean;
  isOwner: boolean;
  isTester: boolean;
  permissions: Permissions;
  canUseFeature: (feature: string) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Приглашение из deep-link: t.me/<bot>?startapp=inv_<token>
  const [inviteToken, setInviteToken] = useState<string | null>(null);

  // ─── Entitlements (тариф): фичи/лимиты для feature-флагов в UI ──────────
  // Бэкенд проверяет доступ ВСЕГДА — здесь только отображение замков.
  const [entitlements, setEntitlements] = useState<EntitlementSummary | null>(null);

  // Перезагружаем тариф при каждом входе/смене роли (onboarding/upgrade/join).
  useEffect(() => {
    if (!user) {
      setEntitlements(null);
      return;
    }
    let cancelled = false;
    api.getMyBillingPlan()
      .then(ent => { if (!cancelled) setEntitlements(ent); })
      .catch(() => { if (!cancelled) setEntitlements(null); });
    return () => { cancelled = true; };
  }, [user]);

  // Фича доступна: системный owner — всегда; иначе по тарифу из бэкенда.
  // Пока entitlements не загружены (или демо-режим) — ничего не блокируем.
  const canUseFeature = useCallback((feature: string): boolean => {
    if (!entitlements) return true;
    if (entitlements.is_system_owner) return true;
    return !!entitlements.features?.[feature];
  }, [entitlements]);

  const login = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const telegramWebApp = window.Telegram?.WebApp;
      const initData = telegramWebApp?.initData;

      if (!initData || initData.trim() === '') {
        // Running outside Telegram — use demo mode
        setUser({
          id: '0',
          telegram_id: '0',
          first_name: 'Демо',
          last_name: 'Пользователь',
          username: 'demo',
          role: 'owner',
          needs_onboarding: false,
          is_active: true,
        });
        setLoading(false);
        return;
      }

      const result = await api.login(initData);
      const token = result.access_token;

      // Токен живёт ТОЛЬКО в памяти (api.ts) — не в localStorage, чтобы его
      // нельзя было украсть при XSS. При перезагрузке страницы вход
      // происходит заново через Telegram initData (бесшовно).
      api.setToken(token);

      // Применяем язык интерфейса: ручной выбор пользователя (language_manual)
      // всегда в приоритете; иначе — язык Telegram из профиля.
      void i18n.changeLanguage(normalizeLang(result.user.language_code));

      setUser({
        ...result.user,
        role: result.user.role as 'owner' | 'admin' | 'tester' | 'user' | 'owner_school' | 'teacher',
      });

      // Invite deep-link (startapp=inv_... / invite_...) → banner offers to join
      try {
        const startParam = telegramWebApp?.initDataUnsafe?.start_param;
        setInviteToken(normalizeInviteToken(startParam));
      } catch {
        setInviteToken(null);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Ошибка авторизации';
      setError(message);
      console.error('[AUTH] Login failed:', message);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setInviteToken(null);
    api.setToken('');
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.close();
    }
  }, []);

  const joinSchool = useCallback(async (token: string) => {
    const res = await api.joinSchool(token);
    if (res.access_token && res.user) {
      // The backend re-issues the token with the (invited) role
      api.setToken(res.access_token);
      setUser({
        ...res.user,
        role: res.user.role as User['role'],
        needs_onboarding: false,
      });
    }
    setInviteToken(null);
  }, []);

  const dismissInvite = useCallback(() => {
    setInviteToken(null);
  }, []);

  const setUserTimezone = useCallback(async (timezone: string) => {
    const updated = await api.updateUserTimezone(timezone);
    setUser(prev => prev ? { ...prev, timezone: updated.timezone || timezone } : prev);
  }, []);

  const setUserCurrency = useCallback(async (currency: string) => {
    const updated = await api.updateUserCurrency(currency);
    setUser(prev => prev ? { ...prev, currency: updated.currency || currency } : prev);
  }, []);

  const setUserLanguage = useCallback(async (language: string) => {
    const updated = await api.updateUserLanguage(language);
    const lang = normalizeLang(updated.language_code || language);
    void i18n.changeLanguage(lang);
    setUser(prev => prev ? { ...prev, language_code: lang, language_manual: true } : prev);
  }, []);

  const setSchoolTimezone = useCallback(async (timezone: string) => {
    await api.updateSchoolTimezone(timezone);
  }, []);

  const completeOnboarding = useCallback(async (choice: 'solo' | 'team', schoolName?: string) => {
    const res = await api.completeOnboarding(choice, schoolName, getDeviceTimezone());

    // The backend re-issues the token with the (possibly upgraded) role
    api.setToken(res.access_token);

    setUser({
      ...res.user,
      role: res.user.role as User['role'],
      needs_onboarding: false,
    });
  }, []);

  const upgradeToSchoolOwner = useCallback(async (schoolName: string) => {
    const res = await api.upgradeToSchoolOwner(schoolName, getDeviceTimezone());

    // New token carries the owner_school role
    api.setToken(res.access_token);

    setUser({
      ...res.user,
      role: res.user.role as User['role'],
      needs_onboarding: false,
    });
  }, []);

  // Auto-login on mount. Токен в localStorage НЕ хранится (безопасность):
  // вход всегда заново через Telegram initData, что бесшовно.
  useEffect(() => {
    login();
  }, [login]);

  const isOwner = user?.role === 'owner';
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const isTester = user?.role === 'tester';
  const permissions = computePermissions(user);

  return (
    <AuthContext.Provider
      value={{
        user, loading, error, login, logout,
        completeOnboarding, upgradeToSchoolOwner,
        setUserTimezone, setUserCurrency, setUserLanguage, setSchoolTimezone,
        inviteToken, joinSchool, dismissInvite,
        isAdmin, isOwner, isTester, permissions,
        canUseFeature,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
